import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import useHelps from "../hooks/useHelps";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ViewKanban from "@mui/icons-material/ViewKanban";
import AlarmIcon from "@material-ui/icons/Alarm";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CakeIcon from "@material-ui/icons/Cake";
import { PhonelinkSetup } from "@material-ui/icons";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";

import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import { i18n } from "../translate/i18n";
import { Campaign } from "@mui/icons-material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import {
  AutoAwesomeRounded,
  AccountBalanceOutlined,
  CalendarMonthOutlined,
  CellTowerOutlined,
  ChatOutlined,
  CloudSyncOutlined,
  ContactPageOutlined,
  DataObjectOutlined,
  ExtensionOutlined,
  GroupsOutlined,
  HandymanOutlined,
  HubOutlined,
  InsightsOutlined,
  Inventory2Outlined,
  MarkChatUnreadOutlined,
  RocketLaunchOutlined,
  RouteOutlined,
  SellOutlined,
  StorefrontOutlined,
  TipsAndUpdatesOutlined,
  TuneOutlined,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";

import useCompanySettings from "../hooks/useSettings/companySettings";
import { estratizaColors, estratizaGradients } from "../styles/estratizaTheme";

const SHOW_BIRTHDAY_SETTINGS_MENU = false;

const useStyles = makeStyles((theme) => ({
  listItem: {
    minHeight: "48px",
    width: "calc(100% - 16px)",
    margin: "3px 8px",
    paddingTop: 5,
    paddingBottom: 5,
    borderRadius: "12px",
    borderLeft: "2px solid transparent",
    color: estratizaColors.goldLight,
    transition: "all 0.22s ease",
    "&:hover": {
      background: "linear-gradient(90deg, rgba(212,175,55,0.16), rgba(212,175,55,0.04))",
      borderLeftColor: "rgba(212,175,55,0.55)",
    },
    "&:hover $iconHoverActive": {
      background: estratizaGradients.gold,
      color: estratizaColors.black,
    },
    "&:hover $listItemText": {
      color: "rgba(255,255,255,0.95)",
      fontWeight: 600,
    },
  },

  listItemText: {
    fontSize: "13.5px",
    color: "#C8B98F",
    fontWeight: 600,
    letterSpacing: "0.012em",
    transition: "color 0.2s ease",
    "& .MuiTypography-root": {
      fontFamily: "'Inter', 'Roboto', sans-serif",
      fontSize: "0.845rem",
      lineHeight: 1.25,
    }
  },

  listItemIcon: {
    minWidth: 46,
  },

  nestedItem: {
    width: "calc(100% - 30px)",
    marginLeft: 22,
    minHeight: 43,
    "& $iconHoverActive": {
      width: 30,
      height: 30,
      borderRadius: 9,
    },
    "& $listItemText, & $listItemText .MuiTypography-root": {
      fontSize: "0.79rem",
    },
  },

  avatarActive: {
    backgroundColor: "transparent",
  },

  avatarHover: {
    backgroundColor: "transparent",
  },

  iconHoverActive: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 11,
    height: 36,
    width: 36,
    background: "linear-gradient(145deg, rgba(242,214,117,0.11), rgba(212,175,55,0.025))",
    border: "1px solid rgba(212,175,55,0.19)",
    color: "#D3C18F",
    transition: "all 0.2s ease",
    "&:hover, &.active": {
      background: estratizaGradients.gold,
      color: estratizaColors.black,
      boxShadow: `0 4px 12px ${theme.palette.primary.main}30`, // Sombra dinâmica
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.4rem", // Mantém tamanho original
      transition: "transform 0.3s ease",
    },
    "&:hover .MuiSvgIcon-root": {
      transform: "scale(1.1)", // Pequena animação no hover
    }
  },

  badge: {
    "& .MuiBadge-badge": {
      animation: "$pulse 1.5s infinite",
      backgroundColor: "#f44336",
      color: "white",
      fontWeight: "bold",
      fontSize: 11,
      minWidth: 18,
      height: 18,
      padding: "0 4px",
    },
  },

  "@keyframes pulse": {
    "0%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(244,67,54,0.7)" },
    "50%": { transform: "scale(1.3)", boxShadow: "0 0 0 8px rgba(244,67,54,0)" },
    "100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(244,67,54,0)" },
  },

  submenuContainer: {
    background: "linear-gradient(90deg, rgba(0,0,0,0.44), rgba(212,175,55,0.025))",
    borderLeft: "1px solid rgba(212,175,55,0.12)",
    marginLeft: 18,
  },

  // Tooltip melhorado
  customTooltip: {
    backgroundColor: theme.mode === "light" ? "#1e293b" : "#374151",
    color: "#fff",
    fontSize: "0.875rem",
    fontWeight: 500,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    "& .MuiTooltip-arrow": {
      color: theme.mode === "light" ? "#1e293b" : "#374151",
    }
  },

  versionContainer: {
    textAlign: "center",
    padding: "10px",
    color: "rgba(212,175,55,0.42)",
    fontSize: "11px",
    fontWeight: 600,
    borderTop: "1px solid rgba(212,175,55,0.12)",
    marginTop: "auto",
  },

  adminSection: {
    "& .MuiListSubheader-root": {
      color: "rgba(242,214,117,0.55)",
      backgroundColor: "transparent",
      fontSize: "0.68rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "1.5px",
      lineHeight: "32px",
    }
  },
  subheaderStyle: {
    color: estratizaColors.goldLight,
    backgroundColor: "rgba(212,175,55,0.12)",
    fontSize: "0.7rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "2px",
    lineHeight: "36px",
    borderLeft: `3px solid ${estratizaColors.gold}`,
    paddingLeft: "12px",
    marginTop: "6px",
    marginBottom: "2px",
  },

  expandIcon: {
    transition: "transform 0.3s ease",
    color: "rgba(255,255,255,0.35)",
    "&.expanded": {
      transform: "rotate(180deg)",
    }
  },

  // Menu container com melhorias sutis
  menuContainer: {
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.mode === "light"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.1)",
      borderRadius: "3px",
      "&:hover": {
        background: theme.mode === "light"
          ? "rgba(0, 0, 0, 0.2)"
          : "rgba(255, 255, 255, 0.2)",
      }
    },
  },

  activeItem: {
    background: "linear-gradient(90deg, rgba(212,175,55,0.22), rgba(212,175,55,0.06))",
    borderLeftColor: estratizaColors.gold,
    "& $iconHoverActive": {
      background: estratizaGradients.gold,
      color: estratizaColors.black,
      boxShadow: "0 6px 16px rgba(155,113,24,0.28)",
    },
    "& $listItemText": {
      color: estratizaColors.goldLight,
      fontWeight: 700,
    }
  }
}));

function ListItemLink(props) {
  const { icon, primary, to, tooltip, showBadge, small } = props;
  const classes = useStyles();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = activeMenu === to || location.pathname === to;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const ConditionalTooltip = ({ children, tooltipEnabled }) =>
    tooltipEnabled ? (
      <Tooltip title={primary} placement="right">
        {children}
      </Tooltip>
    ) : (
      children
    );

  return (
    <ConditionalTooltip tooltipEnabled={!!tooltip}>
      <li>
        <ListItem
          button
          component={renderLink}
          className={`${classes.listItem} ${small ? classes.nestedItem : ""} ${isActive ? classes.activeItem : ""}`}
        >
          {icon ? (
            <ListItemIcon className={classes.listItemIcon}>
              {showBadge ? (
                <Badge
                  badgeContent="!"
                  color="error"
                  overlap="circular"
                  className={classes.badge}
                >
                  <Avatar
                    className={`${classes.iconHoverActive} ${isActive ? "active" : ""
                      }`}
                  >
                    {icon}
                  </Avatar>
                </Badge>
              ) : (
                <Avatar
                  className={`${classes.iconHoverActive} ${isActive ? "active" : ""
                    }`}
                >
                  {icon}
                </Avatar>
              )}
            </ListItemIcon>
          ) : null}
          <ListItemText
            primary={
              <Typography className={classes.listItemText}>
                {primary}
              </Typography>
            }
          />
        </ListItem>
      </li>
    </ConditionalTooltip>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = ({ collapsed, drawerClose }) => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);

  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openCampaignSubmenu, setOpenCampaignSubmenu] = useState(false);
  const [openDashboardSubmenu, setOpenDashboardSubmenu] = useState(false);
  const [openToolsSubmenu, setOpenToolsSubmenu] = useState(() =>
    ["/follow-up", "/disparos", "/quick-messages", "/schedules"].some((path) =>
      location.pathname.startsWith(path)
    )
  );
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showWavoipCall, setShowWavoipCall] = useState(false);

  // novas features
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [unreadsCount, setUnreadsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [version, setVersion] = useState(false);
  const [managementHover, setManagementHover] = useState(false);
  const [campaignHover, setCampaignHover] = useState(false);
  const { list } = useHelps(); // INSERIR
  const [hasHelps, setHasHelps] = useState(false);

  const [openFlowSubmenu, setOpenFlowSubmenu] = useState(false);
  const [flowHover, setFlowHover] = useState(false);

  const { get: getSetting } = useCompanySettings();
  const [showWallets, setShowWallets] = useState(false);

  const isFlowbuilderRouteActive =
    location.pathname.startsWith("/phrase-lists");
  location.pathname.startsWith("/flowbuilders");

  useEffect(() => {
    // INSERIR ESSE EFFECT INTEIRO
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting(
          {
            "column": "DirectTicketsToWallets"
          }
        );

        setShowWallets(setting.DirectTicketsToWallets);

      } catch (err) {
        toastError(err);
      }
    }

    fetchSettings();
  }, [setShowWallets]);

  const isManagementActive =
    location.pathname === "/" ||
    location.pathname.startsWith("/reports") ||
    location.pathname.startsWith("/moments");

  const isCampaignRouteActive =
    location.pathname === "/campaigns";

  const isToolsRouteActive =
    location.pathname.startsWith("/follow-up") ||
    location.pathname.startsWith("/disparos") ||
    location.pathname.startsWith("/quick-messages") ||
    location.pathname.startsWith("/schedules");

  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  const { getPlanCompany } = usePlans();

  const { getVersion } = useVersion();

  useEffect(() => {
    async function fetchVersion() {
      const _version = await getVersion();
      setVersion(_version.version);
    }
    fetchVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowCampaigns(planConfigs.plan.useCampaigns);
      setShowKanban(planConfigs.plan.useKanban);
      setShowOpenAi(planConfigs.plan.useOpenAi);
      setShowIntegrations(planConfigs.plan.useIntegrations);
      setShowSchedules(planConfigs.plan.useSchedules);
      setShowInternalChat(planConfigs.plan.useInternalChat);
      setShowExternalApi(planConfigs.plan.useExternalApi);
      setShowWavoipCall(planConfigs.plan.wavoip);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id && socket && typeof socket.on === 'function') {
      const companyId = user.companyId;

      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      const eventName = `company-${companyId}-chat`;
      console.log('Registrando listener para:', eventName);

      socket.on(eventName, onCompanyChatMainListItems);

      return () => {
        if (socket && typeof socket.off === 'function') {
          console.log('Removendo listener para:', eventName);
          socket.off(eventName, onCompanyChatMainListItems);
        }
      };
    }
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    setUnreadsCount(unreadsCount);
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  // useEffect(() => {
  //   if (localStorage.getItem("cshow")) {
  //     setShowCampaigns(true);
  //   }
  // }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return whats.status !== "CONNECTED";
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div onClick={drawerClose}>
      {user.showDashboard === "enabled" && (
          <>
            <Tooltip
              title={collapsed ? i18n.t("mainDrawer.listItems.management") : ""}
              placement="right"
            >
              <ListItem
                dense
                button
                className={`${classes.listItem} ${isManagementActive ? classes.activeItem : ""}`}
                onClick={() => setOpenDashboardSubmenu((prev) => !prev)}
                onMouseEnter={() => setManagementHover(true)}
                onMouseLeave={() => setManagementHover(false)}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Avatar
                    className={`${classes.iconHoverActive} ${isManagementActive || managementHover ? "active" : ""
                      }`}
                  >
                    <WorkspacePremiumOutlined />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography className={classes.listItemText}>
                      {i18n.t("mainDrawer.listItems.management")}
                    </Typography>
                  }
                />
                {openDashboardSubmenu ? <ExpandLessIcon style={{ color: "rgba(255,255,255,0.4)" }} /> : <ExpandMoreIcon style={{ color: "rgba(255,255,255,0.4)" }} />}
              </ListItem>
            </Tooltip>
            <Collapse
              in={openDashboardSubmenu}
              timeout="auto"
              unmountOnExit
              className={classes.submenuContainer}
            >
              {user.showDashboard === "enabled" && (
                  <>
                    <ListItemLink
                      small
                      to="/"
                      primary="Dashboard"
                      icon={<InsightsOutlined />}
                      tooltip={collapsed}
                    />
                    <ListItemLink
                      small
                      to="/reports"
                      primary={i18n.t("mainDrawer.listItems.reports")}
                      icon={<DataObjectOutlined />}
                      tooltip={collapsed}
                    />
                    <ListItemLink
                      small
                      to="/live-control"
                      primary="Controle ao Vivo"
                      icon={<MonitorHeartIcon />}
                      tooltip={collapsed}
                    />
                    <ListItemLink
                      small
                      to="/ranking"
                      primary={<><span style={{display:"block",lineHeight:1.2}}>Métricas de</span><span style={{display:"block",lineHeight:1.2,fontSize:"0.85em"}}>Atendimento</span></>}
                      icon={<WorkspacePremiumOutlined />}
                      tooltip={collapsed}
                    />
                    <ListItemLink
                      small
                      to="/vendas"
                      primary="Vendas"
                      icon={<StorefrontOutlined />}
                      tooltip={collapsed}
                    />
                    {user.profile === "admin" && (
                      <ListItemLink
                        small
                        to="/controle-leads"
                        primary="Controle de Leads"
                        icon={<RouteOutlined />}
                        tooltip={collapsed}
                      />
                    )}
                  </>
              )}

              {user.profile === "admin" && (
                <ListItemLink
                  small
                  to="/plugins/floup/dashboard"
                  primary="Painel FollowUP"
                  icon={<ViewKanban />}
                  tooltip={collapsed}
                />
              )}
              {user.profile === "admin" && showWallets && (
                <>
                  <ListItemLink
                    to="/wallets"
                    primary={i18n.t("mainDrawer.listItems.wallets")}
                    icon={<AccountBalanceWalletIcon />}
                    tooltip={collapsed}
                  />
                </>
              )}
            </Collapse>
          </>
      )}

      {showKanban && (
        <>
          <ListItemLink
            to="/kanban"
            primary={i18n.t("mainDrawer.listItems.kanban")}
            icon={<HubOutlined />}
            tooltip={collapsed}
          />
        </>
      )}

      <ListItemLink
        to="/tickets"
        primary="Chat"
        icon={<ChatOutlined />}
        tooltip={collapsed}
      />

      {user.showFlow === "enabled" && (
        <ListItemLink
          to="/flowbuilders"
          primary={"Fluxos de Atendimento"}
          icon={<RouteOutlined />}
          tooltip={collapsed}
        />
      )}

      {user.showFlow === "enabled" && (
        <ListItemLink
          to="/ai-agents"
          primary={"Agentes de IA"}
          icon={<AutoAwesomeRounded />}
          tooltip={collapsed}
        />
      )}

      <Tooltip title={collapsed ? "Ferramentas" : ""} placement="right">
        <ListItem
          button
          className={`${classes.listItem} ${isToolsRouteActive ? classes.activeItem : ""}`}
          onClick={() => setOpenToolsSubmenu((prev) => !prev)}
        >
          <ListItemIcon className={classes.listItemIcon}>
            <Avatar className={`${classes.iconHoverActive} ${isToolsRouteActive ? "active" : ""}`}>
              <HandymanOutlined />
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={<Typography className={classes.listItemText}>Ferramentas</Typography>}
          />
          {openToolsSubmenu ? (
            <ExpandLessIcon style={{ color: "rgba(242,214,117,0.65)" }} />
          ) : (
            <ExpandMoreIcon style={{ color: "rgba(242,214,117,0.65)" }} />
          )}
        </ListItem>
      </Tooltip>

      <Collapse
        in={openToolsSubmenu}
        timeout="auto"
        unmountOnExit
        className={classes.submenuContainer}
      >
        <List component="div" disablePadding>
          <ListItemLink
            small
            to="/follow-up"
            primary="Follow Up Estratiza"
            icon={<RouteOutlined />}
            tooltip={collapsed}
          />
          {(user.profile === "admin" || user.profile === "super" || user.super || user.showCampaign === "enabled") && (
            <ListItemLink
              small
              to="/disparos"
              primary="Disparos em Massa"
              icon={<RocketLaunchOutlined />}
              tooltip={collapsed}
            />
          )}
          <ListItemLink
            small
            to="/quick-messages"
            primary="Respostas Rápidas"
            icon={<TipsAndUpdatesOutlined />}
            tooltip={collapsed}
          />
          {showSchedules && (
            <ListItemLink
              small
              to="/schedules"
              primary="Agendamentos"
              icon={<CalendarMonthOutlined />}
              tooltip={collapsed}
            />
          )}
        </List>
      </Collapse>

      <ListItemLink
        to="/reminders"
        primary="Meus Lembretes"
        icon={<AlarmIcon />}
        tooltip={collapsed}
      />

      <ListItemLink
        to="/template-manager"
        primary="Templates Meta"
        icon={<Inventory2Outlined />}
        tooltip={collapsed}
      />

      {user.showContacts === "enabled" && (
        <ListItemLink
          to="/contacts"
          primary={i18n.t("mainDrawer.listItems.contacts")}
          icon={<ContactPageOutlined />}
          tooltip={collapsed}
        />
      )}

      <ListItemLink
        to="/tags"
        primary={i18n.t("mainDrawer.listItems.tags")}
        icon={<SellOutlined />}
        tooltip={collapsed}
      />

      {showInternalChat && (
        <>
          <ListItemLink
            to="/chats"
            primary={i18n.t("mainDrawer.listItems.chats")}
            icon={
              <Badge badgeContent={invisible ? 0 : unreadsCount} className={invisible ? "" : classes.badge} invisible={invisible}>
                <MarkChatUnreadOutlined />
              </Badge>
            }
            tooltip={collapsed}
          />
        </>
      )}

      {/* 
      <ListItemLink
        to="/todolist"
        primary={i18n.t("ToDoList")}
        icon={<EventAvailableIcon />}
      /> 
      */}

      {hasHelps && (
        <ListItemLink
          to="/helps"
          primary={i18n.t("mainDrawer.listItems.helps")}
          icon={<HelpOutlineIcon />}
          tooltip={collapsed}
        />
      )}


            {(user.profile === "admin" || (user.allowConnections === "enabled" || user.allowConnections === "own")) && (
          <>
            <Divider style={{ borderColor: "rgba(212,175,55,0.24)", marginTop: 10 }} />
            <ListSubheader disableSticky className={classes.subheaderStyle}>
              GESTÃO · {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>

            {user.super && (
              <ListItemLink
                to="/announcements"
                primary={i18n.t("mainDrawer.listItems.annoucements")}
                icon={<Campaign />}
                tooltip={collapsed}
              />
            )}

            {showExternalApi && user.profile === "admin" && (
              <ListItemLink
                to="/messages-api"
                primary={i18n.t("mainDrawer.listItems.messagesAPI")}
                icon={<DataObjectOutlined />}
                tooltip={collapsed}
              />
            )}

            {user.profile === "admin" && (
              <ListItemLink
                to="/users"
                primary={i18n.t("mainDrawer.listItems.users")}
                icon={<GroupsOutlined />}
                tooltip={collapsed}
              />
            )}

            {SHOW_BIRTHDAY_SETTINGS_MENU && user.profile === "admin" && (
              <ListItemLink
                to="/birthday-settings"
                primary={i18n.t("mainDrawer.listItems.birthdaySettings")}
                icon={<CakeIcon />}
                tooltip={collapsed}
              />
            )}

            {user.profile === "admin" && (
              <ListItemLink
                to="/queues"
                primary={i18n.t("mainDrawer.listItems.queues")}
                icon={<HubOutlined />}
                tooltip={collapsed}
              />
            )}


            {/* Talk.Ai hidden — use Flow Builder AI Agent instead */}

            {showIntegrations && user.profile === "admin" && (
              <ListItemLink
                to="/queue-integration"
                primary={i18n.t("mainDrawer.listItems.queueIntegration")}
                icon={<ExtensionOutlined />}
                tooltip={collapsed}
              />
            )}

            {(user.profile === "admin" || (user.allowConnections === "enabled" || user.allowConnections === "own")) && (
              <ListItemLink
                to="/connections"
                primary={i18n.t("mainDrawer.listItems.connections")}
                icon={<CellTowerOutlined />}
                showBadge={connectionWarning}
                tooltip={collapsed}
              />
            )}
            {user.super && (
              <ListItemLink
                to="/allConnections"
                primary={i18n.t("mainDrawer.listItems.allConnections")}
                icon={<PhonelinkSetup />}
                tooltip={collapsed}
              />
            )}
            {user.profile === "admin" && (
              <ListItemLink
                to="/financeiro"
                primary={i18n.t("mainDrawer.listItems.financeiro")}
                icon={<AccountBalanceOutlined />}
                tooltip={collapsed}
              />
            )}
            {user.profile === "admin" && (
              <ListItemLink
                to="/settings"
                primary={i18n.t("mainDrawer.listItems.settings")}
                icon={<TuneOutlined />}
                tooltip={collapsed}
              />
            )}
            {user.super && (
              <ListItemLink
                to="/companies"
                primary={i18n.t("mainDrawer.listItems.companies")}
                icon={<WorkspacePremiumOutlined />}
                tooltip={collapsed}
              />
            )}
            {user.super && (
              <ListItemLink
                to="/ai-usage"
                primary="Consumo de IA"
                icon={<InsightsOutlined />}
                tooltip={collapsed}
              />
            )}
            {user.super && (
              <ListItemLink
                to="/backup"
                primary="Backup do Sistema"
                icon={<CloudSyncOutlined />}
                tooltip={collapsed}
              />
            )}
          </>
      )}
      {/* Servicos Estratiza - visivel para todos */}
      <Divider style={{ borderColor: "rgba(212,175,55,0.18)", marginTop: 8, marginBottom: 4 }} />
      <ListItemLink
        to="/estratiza-services"
        primary="Serviços Estratiza"
        icon={<StorefrontOutlined />}
        tooltip={collapsed}
      />

      {!collapsed && (
        <React.Fragment>
          <Divider />
          <Typography
            style={{
              fontSize: "12px",
              padding: "10px",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            2026.{`${version}`}
          </Typography>
        </React.Fragment>
      )}
    </div>
  );
};

export default MainListItems;
